import { Icon } from '@raro/ui';
import type { MockupFrame } from '@raro/domain';
import type { ReactNode } from 'react';

/**
 * Moldura de canal: a peça no lugar em que vai viver. Só CSS, sem logos de terceiros.
 * O conteúdo (canvas de revisão) entra como `children`; a moldura nunca intercepta os cliques.
 */
export function Mockup({ frame, deliverable, organizationName, channelHandle, children }: { frame: MockupFrame; deliverable: { title: string; description: string | null }; organizationName: string; channelHandle: string | null; children: ReactNode }) {
  const handle = channelHandle ?? organizationName.toLowerCase().replace(/\s+/g, '');
  const initial = organizationName.slice(0, 1).toUpperCase() || 'R';
  const caption = deliverable.description ?? deliverable.title;
  switch (frame) {
    case 'instagram_feed':
    case 'facebook_feed':
      return (
        <div className="rr-mock rr-mock--feed">
          <div className="rr-mock__top"><span className="rr-mock__avatar">{initial}</span><div><b>{handle}</b><span>patrocinado</span></div><span className="rr-mock__dots">···</span></div>
          <div className="rr-mock__media">{children}</div>
          <div className="rr-mock__actions"><Icon name="heart" size={18} /><Icon name="comment" size={18} /><Icon name="send" size={18} /><span className="rr-mock__spacer" /><Icon name="bookmark" size={18} /></div>
          <div className="rr-mock__caption"><b>{handle}</b> {caption}</div>
        </div>
      );
    case 'instagram_story':
    case 'instagram_reel':
    case 'tiktok':
      return (
        <div className={`rr-mock rr-mock--vertical rr-mock--${frame}`}>
          <div className="rr-mock__phone">
            {frame === 'instagram_story' && <div className="rr-mock__progress"><span /><span className="rr-mock__progress--on" /><span /></div>}
            <div className="rr-mock__top rr-mock__top--over"><span className="rr-mock__avatar">{initial}</span><b>{handle}</b><span className="rr-mock__dots">···</span></div>
            <div className="rr-mock__media rr-mock__media--fill">{children}</div>
            {frame !== 'instagram_story' && (
              <>
                <div className="rr-mock__side"><Icon name="heart" size={22} /><Icon name="comment" size={22} /><Icon name="send" size={22} /><Icon name="bookmark" size={22} /></div>
                <div className="rr-mock__caption rr-mock__caption--over"><b>{handle}</b> {caption}</div>
              </>
            )}
            {frame === 'instagram_story' && <div className="rr-mock__reply">Enviar mensagem</div>}
          </div>
        </div>
      );
    case 'linkedin_feed':
      return (
        <div className="rr-mock rr-mock--feed rr-mock--linkedin">
          <div className="rr-mock__top"><span className="rr-mock__avatar rr-mock__avatar--square">{initial}</span><div><b>{organizationName}</b><span>1.284 seguidores · Promovido</span></div></div>
          <div className="rr-mock__caption rr-mock__caption--first">{caption}</div>
          <div className="rr-mock__media">{children}</div>
          <div className="rr-mock__actions rr-mock__actions--text"><span>Gostei</span><span>Comentar</span><span>Compartilhar</span><span>Enviar</span></div>
        </div>
      );
    case 'youtube':
      return (
        <div className="rr-mock rr-mock--youtube">
          <div className="rr-mock__media rr-mock__media--wide">{children}</div>
          <div className="rr-mock__yt"><span className="rr-mock__avatar">{initial}</span><div><b>{deliverable.title}</b><span>{organizationName} · 2,1 mil visualizações · há 1 dia</span></div></div>
        </div>
      );
    case 'whatsapp':
      return (
        <div className="rr-mock rr-mock--whatsapp">
          <div className="rr-mock__chat-top"><span className="rr-mock__avatar">{initial}</span><b>{organizationName}</b></div>
          <div className="rr-mock__bubble">
            <div className="rr-mock__media">{children}</div>
            <p>{caption}</p>
            <span className="rr-mock__time">10:42 <Icon name="check-double" size={12} /></span>
          </div>
        </div>
      );
    case 'email':
      return (
        <div className="rr-mock rr-mock--email">
          <div className="rr-mock__mail-head"><b>{deliverable.title}</b><span>{organizationName} &lt;contato@{handle}.com.br&gt; · para mim</span></div>
          <div className="rr-mock__media rr-mock__media--doc">{children}</div>
        </div>
      );
    case 'website':
      return (
        <div className="rr-mock rr-mock--browser">
          <div className="rr-mock__chrome"><span /><span /><span /><div className="rr-mock__url">{handle}.com.br</div></div>
          <div className="rr-mock__media rr-mock__media--doc">{children}</div>
        </div>
      );
    case 'pdv':
      return (
        <div className="rr-mock rr-mock--pdv">
          <div className="rr-mock__shelf" />
          <div className="rr-mock__media rr-mock__media--doc">{children}</div>
          <div className="rr-mock__shelf rr-mock__shelf--bottom" />
        </div>
      );
    default:
      return <>{children}</>;
  }
}
